import { useEffect, useRef, useState } from "react";
import { masterApi, apiError, formatBRL, formatPhone } from "@/lib/api";
import { useUnit } from "@/pages/Master/MasterApp";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { modal } from "@/lib/modal";
import { copiarTexto } from "@/lib/clipboard";
import Pagination from "@/components/crud/Pagination";
import { Search, CheckCheck, BellRing, Droplets, Car, Copy, ExternalLink, AlertTriangle } from "lucide-react";

const PAGE_SIZE = 50;
const fmtData = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");
const fmtDataHora = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");
const fmtPct = (v) => `${Number(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;

const STATUS = {
  disponivel: { label: "Disponível", cls: "chip-yellow" },
  usado: { label: "Usado", cls: "chip-green" },
  expirado: { label: "Expirado", cls: "chip-gray" },
};

const FILTROS = [
  ["", "Todos"], ["disponivel", "Disponíveis"], ["usado", "Usados"], ["expirado", "Expirados"],
];

export default function MasterTrocaOleo() {
  const { unitId } = useUnit();
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");          // texto digitado
  const [busca, setBusca] = useState("");  // busca aplicada (Enter / Buscar)
  const [inicio, setInicio] = useState("");
  const [fim, setFim] = useState("");
  const [items, setItems] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [busy, setBusy] = useState(null); // id do prêmio com ação em andamento
  // Plano B da cópia (o navegador bloqueou): o link fica num campo só de leitura, já selecionado.
  const [linkManual, setLinkManual] = useState(null);
  const linkRef = useRef(null);
  const selecionarLink = () => {
    const ta = linkRef.current;
    if (!ta) return;
    try { ta.focus(); ta.select(); ta.setSelectionRange(0, ta.value.length); } catch (_) { /* navegador antigo */ }
  };
  useEffect(() => {
    if (!linkManual) return undefined;
    const t = setTimeout(selecionarLink, 30);
    return () => clearTimeout(t);
  }, [linkManual]);

  // Data digitada inválida (ex.: ano com 5 dígitos) é ignorada, sem derrubar a tela.
  const toIso = (txt) => {
    if (!/^\d{4}-\d{2}-\d{2}T/.test(txt)) return null;
    const d = new Date(txt);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  };
  const periodo = () => {
    const p = new URLSearchParams();
    if (unitId && unitId !== "all") p.set("unit_id", unitId);
    const ini = inicio ? toIso(`${inicio}T00:00:00`) : null;
    const fi = fim ? toIso(`${fim}T23:59:59.999`) : null;
    if (ini) p.set("inicio", ini);
    if (fi) p.set("fim", fi);
    return p;
  };

  const load = async (targetPage = 1) => {
    try {
      const p = periodo();
      const pl = new URLSearchParams(p);
      pl.set("page", String(targetPage)); pl.set("page_size", String(PAGE_SIZE));
      if (status) pl.set("status", status);
      if (busca.trim()) pl.set("q", busca.trim());
      const [lista, res] = await Promise.all([
        masterApi.get(`/master/troca-oleo/premios?${pl.toString()}`),
        masterApi.get(`/master/troca-oleo/resumo?${p.toString()}`),
      ]);
      setItems(lista.data.items || []);
      setTotal(lista.data.total || 0);
      setTotalPages(lista.data.total_pages || 0);
      setPage(lista.data.page || targetPage);
      setResumo(res.data);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(1); }, [unitId, status, inicio, fim, busca]);

  const marcarUsado = async (it) => {
    const ok = await modal.confirm({
      title: "Marcar desconto como já usado",
      message: `O prêmio de Troca de Óleo de ${it.cliente_nome} será marcado como USADO e o botão "Utilizar" deixará de aparecer no atendimento. Nada é lançado no caixa.`,
      confirmText: "Marcar como usado",
      tone: "danger",
    });
    if (!ok) return;
    setBusy(it.id);
    try {
      await masterApi.post(`/master/troca-oleo/premios/${it.id}/marcar-usado`, {});
      modal.success("Desconto marcado como usado.");
      load(page);
    } catch (e) { modal.error(apiError(e)); }
    finally { setBusy(null); }
  };

  const lembrar = async (it) => {
    const ok = await modal.confirm({
      title: "Enviar lembrete",
      message: `Enviar o lembrete para ${formatPhone(it.cliente_telefone)} pela rota escolhida em Mensagens automáticas > Envio das mensagens (SMS ou WhatsApp). Texto do SMS: "${it.mensagem_lembrete}"`,
      confirmText: "Enviar",
    });
    if (!ok) return;
    setBusy(it.id);
    try {
      const { data } = await masterApi.post(`/master/troca-oleo/premios/${it.id}/lembrete-sms`, {});
      const canal = { sms: "SMS", whatsapp: "WhatsApp" }[data?.canal] || "mensagem";
      modal.success(`Lembrete enviado por ${canal} para ${it.cliente_nome}.`);
      load(page);
    } catch (e) { modal.error(apiError(e)); }
    finally { setBusy(null); }
  };

  // Item 4 (19/10): link wa.me com o texto pronto (gerado no backend, só para prêmio disponível
  // com telefone válido). Copia pelo navigator.clipboard; plano B: textarea + execCommand.
  const copiarLink = async (it) => {
    const ok = await copiarTexto(it.whatsapp_link);
    const detalhes = [
      { label: "Cliente", value: `${it.cliente_nome || "—"}${it.cliente_placa ? ` · ${it.cliente_placa}` : ""}` },
      { label: "Telefone", value: formatPhone(it.cliente_telefone) || "—" },
      { label: "Vence em", value: fmtData(it.valido_ate) },
    ];
    if (ok) {
      modal.success({
        title: "Link copiado",
        message: `Cole o link no WhatsApp para abrir a conversa com ${it.cliente_nome || "o cliente"} já com a mensagem pronta.`,
        details: [...detalhes, ...(it.mensagem_whatsapp ? [{ label: "Mensagem", value: it.mensagem_whatsapp }] : [])],
      });
    } else {
      // O link (um texto longo, sem espaços) não cabe numa linha de detalhe do modal global:
      // vai num campo de várias linhas, já selecionado, com o botão de abrir no WhatsApp.
      setLinkManual(it);
    }
  };

  const abrirLink = (it) => {
    try { window.open(it.whatsapp_link, "_blank", "noopener,noreferrer"); } catch (_) { /* bloqueado pelo navegador */ }
  };

  const validade = (it) => {
    if (it.status !== "disponivel") return <span className="text-[11px] text-neutral-500">{fmtData(it.valido_ate)}</span>;
    if (!it.valido_ate) return <span className="text-[11px] text-neutral-500">Sem vencimento</span>;
    const d = it.dias_restantes;
    const txt = d <= 0 ? "Vence hoje" : `Faltam ${d} dia${d === 1 ? "" : "s"}`;
    return (
      <div className="leading-tight">
        <div className="text-[12px]">{fmtData(it.valido_ate)}</div>
        <div className={`text-[10.5px] font-semibold ${d <= 5 ? "text-rose-600" : "text-amber-700"}`}>{txt}</div>
      </div>
    );
  };

  const uso = (it) => {
    if (it.status === "usado") {
      const origem = it.uso_origem === "manual" ? "Marcado pelo Master" : `No caixa${it.unit_uso_nome ? ` · ${it.unit_uso_nome}` : ""}`;
      return (
        <div className="leading-tight">
          <div className="text-[12px]">{fmtDataHora(it.usado_em)}</div>
          <div className="text-[10.5px] text-neutral-500">{origem}</div>
          {it.uso_origem === "resgate" && it.valor_final != null && (
            <div className="text-[10.5px] text-neutral-600">
              {formatBRL(it.valor_sem_desconto)} → <b>{formatBRL(it.valor_final)}</b> (−{formatBRL(it.desconto_reais)})
            </div>
          )}
        </div>
      );
    }
    if (it.status === "expirado") return <span className="text-[11px] text-neutral-500">Não utilizado</span>;
    return <span className="text-[11px] text-neutral-400">—</span>;
  };

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-troca-oleo">
      <div>
        <div className="chip mb-1.5 text-[10px] py-0.5">Gestão</div>
        <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Troca de Óleo</h1>
        <p className="text-[12px] text-neutral-500 mt-1">
          Prêmios de desconto na Troca de Óleo conquistados pelos clientes e como estão sendo utilizados.
        </p>
      </div>

      {resumo && (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2.5" data-testid="oleo-resumo">
          <Kpi label="Prêmios ganhos" value={resumo.ganhos} />
          <Kpi label="Usados" value={resumo.usados}
               sub={`${resumo.usados_caixa} no caixa · ${resumo.usados_manual} marcado${resumo.usados_manual === 1 ? "" : "s"}`} />
          <Kpi label="Disponíveis" value={resumo.disponiveis} sub="aguardando uso" />
          <Kpi label="Expirados" value={resumo.expirados} sub="venceram sem uso" />
          <Kpi label="Taxa de utilização" value={fmtPct(resumo.taxa_utilizacao)} highlight
               sub={`${fmtPct(resumo.taxa_utilizacao_encerrados)} dos já encerrados`} testid="oleo-taxa" />
          <Kpi label="Receita gerada" value={formatBRL(resumo.receita_gerada)}
               sub="cobrado nas trocas com desconto" testid="oleo-receita" />
          <Kpi label="Desconto concedido" value={formatBRL(resumo.desconto_concedido)}
               sub={`sobre ${formatBRL(resumo.valor_sem_desconto)} sem desconto`} testid="oleo-desconto" />
        </div>
      )}

      <div className="card-soft rounded-xl p-3 flex flex-wrap gap-3 items-end" data-testid="oleo-filtros">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Ganho de</label>
          <input type="date" data-testid="oleo-inicio" value={inicio} onChange={(e) => setInicio(e.target.value)}
                 className="mt-1 h-9 rounded-md bg-white border border-neutral-200 px-2 text-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">até</label>
          <input type="date" data-testid="oleo-fim" value={fim} onChange={(e) => setFim(e.target.value)}
                 className="mt-1 h-9 rounded-md bg-white border border-neutral-200 px-2 text-sm" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Cliente</label>
          <div className="mt-1 flex gap-1.5">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
              <input data-testid="oleo-busca" value={q} onChange={(e) => setQ(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && (busca === q ? load(1) : setBusca(q))}
                     placeholder="Nome, placa ou telefone"
                     className="w-full h-9 rounded-md bg-white border border-neutral-200 pl-8 pr-2 text-sm outline-none focus:border-[color:var(--brand-blue)]" />
            </div>
            <button data-testid="oleo-buscar" onClick={() => (busca === q ? load(1) : setBusca(q))} className="btn-primary-blue h-9 px-3 text-[12px] font-medium shrink-0">
              Buscar
            </button>
          </div>
        </div>
        {(inicio || fim || q || busca) && (
          <button onClick={() => { setInicio(""); setFim(""); setQ(""); setBusca(""); }}
                  className="btn-ghost h-9 px-3 text-[11px] font-medium">Limpar</button>
        )}
      </div>

      <div className="flex gap-1 p-1 bg-white/80 border border-neutral-200/70 rounded-full w-fit overflow-x-auto max-w-full" data-testid="oleo-status-tabs">
        {FILTROS.map(([k, label]) => (
          <button key={k || "todos"} data-testid={`oleo-status-${k || "todos"}`} onClick={() => setStatus(k)}
                  className={`px-3.5 h-8 rounded-full text-[11.5px] font-medium whitespace-nowrap transition-colors ${status === k ? "bg-neutral-900 text-white" : "text-neutral-600 hover:bg-neutral-100"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="card-soft rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Cliente</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Unidade</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Ganho em</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Validade</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Situação</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Utilização</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Lembretes</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.id} data-testid={`oleo-row-${it.id}`}>
                <TableCell>
                  <div className="font-medium leading-tight flex items-center gap-1.5">
                    {it.cliente_nome || "—"}
                    {it.categoria === "app_driver" && <Car size={12} className="text-amber-700" title="Motorista de App" />}
                  </div>
                  <div className="text-[10.5px] text-neutral-500">
                    {it.cliente_placa || "—"}{it.cliente_telefone ? ` · ${formatPhone(it.cliente_telefone)}` : ""}
                    {!it.cliente_existe && " · cadastro excluído"}
                  </div>
                </TableCell>
                <TableCell className="text-[12px]">{it.unit_nome || "—"}</TableCell>
                <TableCell className="text-[12px] whitespace-nowrap">{fmtData(it.ganho_em)}</TableCell>
                <TableCell className="whitespace-nowrap">{validade(it)}</TableCell>
                <TableCell>
                  <span className={`chip ${STATUS[it.status]?.cls || "chip-gray"} text-[10px] py-0.5`}>
                    {STATUS[it.status]?.label || it.status}
                  </span>
                </TableCell>
                <TableCell>{uso(it)}</TableCell>
                <TableCell className="text-[11px] whitespace-nowrap">
                  {it.lembretes_enviados > 0
                    ? <span title={`Último: ${fmtDataHora(it.ultimo_lembrete_em)}`}>{it.lembretes_enviados} · {fmtData(it.ultimo_lembrete_em)}</span>
                    : <span className="text-neutral-400">—</span>}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap space-x-1">
                  {it.whatsapp_link && (
                    <>
                      <button data-testid={`oleo-wa-copiar-${it.id}`} title="Copiar link do WhatsApp com a mensagem pronta"
                              onClick={() => copiarLink(it)}
                              className="btn-ghost h-8 px-2.5 text-[10.5px] font-semibold uppercase tracking-wide inline-flex items-center gap-1 align-middle">
                        <Copy size={12} /> Copiar link WhatsApp
                      </button>
                      <button data-testid={`oleo-wa-abrir-${it.id}`} title="Abrir a conversa no WhatsApp em nova aba"
                              onClick={() => abrirLink(it)}
                              className="icon-action icon-action--green align-middle"><ExternalLink size={15} /></button>
                    </>
                  )}
                  {it.status === "disponivel" && it.cliente_existe && (
                    <>
                      <button data-testid={`oleo-sms-${it.id}`} title="Lembrar quanto tempo falta para vencer (SMS ou WhatsApp, conforme Mensagens automáticas)"
                              disabled={busy === it.id} onClick={() => lembrar(it)}
                              className="icon-action icon-action--amber disabled:opacity-40"><BellRing size={15} /></button>
                      <button data-testid={`oleo-usado-${it.id}`} title="Marcar como já usado"
                              disabled={busy === it.id} onClick={() => marcarUsado(it)}
                              className="icon-action icon-action--green disabled:opacity-40"><CheckCheck size={15} /></button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-neutral-500 py-8">
                  <Droplets size={18} className="inline-block mb-1 text-neutral-300" /><br />
                  Nenhum prêmio de Troca de Óleo {status ? `"${FILTROS.find(([k]) => k === status)?.[1].toLowerCase()}"` : ""} encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination page={page} totalPages={totalPages} total={total} onPage={load} unitLabel="prêmios" />

      <Dialog open={!!linkManual} onOpenChange={(v) => !v && setLinkManual(null)}>
        <DialogContent className="max-w-md rounded-2xl border-none p-0 overflow-y-auto"
                       onOpenAutoFocus={(e) => { e.preventDefault(); selecionarLink(); }}>
          {linkManual && (
            <div className="bg-white p-6 rounded-2xl space-y-3" data-testid="oleo-wa-manual">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center shrink-0">
                  <AlertTriangle size={16} />
                </div>
                <h2 className="font-serif-display text-xl leading-tight">Não foi possível copiar</h2>
              </div>
              <p className="text-[12.5px] text-neutral-600 leading-snug">
                O navegador bloqueou a cópia automática. O link abaixo já está selecionado: copie
                (Ctrl+C, ou toque e segure no celular) e cole no WhatsApp. Ou abra a conversa direto.
              </p>
              <div className="rounded-lg border border-neutral-200 divide-y divide-neutral-100 text-[12px]">
                <div className="flex items-center justify-between gap-3 px-3 py-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold shrink-0">Cliente</span>
                  <span className="font-semibold text-right min-w-0 break-words">
                    {linkManual.cliente_nome || "—"}{linkManual.cliente_placa ? ` · ${linkManual.cliente_placa}` : ""}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 px-3 py-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold shrink-0">Telefone</span>
                  <span className="font-semibold">{formatPhone(linkManual.cliente_telefone) || "—"}</span>
                </div>
                <div className="flex items-center justify-between gap-3 px-3 py-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold shrink-0">Vence em</span>
                  <span className="font-semibold">{fmtData(linkManual.valido_ate)}</span>
                </div>
              </div>
              <div>
                <label htmlFor="oleo-wa-link-manual" className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">Link</label>
                <textarea id="oleo-wa-link-manual" ref={linkRef} data-testid="oleo-wa-link-manual" readOnly rows={8}
                          value={linkManual.whatsapp_link || ""} onFocus={(e) => e.target.select()} onClick={(e) => e.target.select()}
                          className="mt-1 w-full rounded-lg border border-neutral-200 bg-neutral-50 px-2.5 py-2 text-[11px] font-mono leading-snug resize-none break-all outline-none focus:border-[color:var(--brand-blue)]"
                          style={{ overflowWrap: "anywhere", wordBreak: "break-all" }} />
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <button data-testid="oleo-wa-manual-fechar" onClick={() => setLinkManual(null)}
                        className="btn-ghost h-11 text-sm font-medium">Fechar</button>
                <button data-testid="oleo-wa-manual-abrir" onClick={() => abrirLink(linkManual)}
                        className="btn-primary-blue h-11 text-sm font-medium inline-flex items-center justify-center gap-1.5">
                  <ExternalLink size={14} /> Abrir no WhatsApp
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, sub, highlight, testid }) {
  return (
    <div data-testid={testid} className="card-soft rounded-xl p-3"
         style={highlight ? { background: "linear-gradient(135deg,#FFFBEB 0%,#FEF3C7 100%)", borderColor: "#FCD34D" } : undefined}>
      <div className="text-[9.5px] uppercase tracking-widest text-neutral-500 leading-tight">{label}</div>
      <div className="font-display font-black text-2xl leading-none mt-1 text-[color:var(--ink)] number-tabular">{value}</div>
      {sub && <div className="text-[10.5px] text-neutral-500 mt-1 leading-tight">{sub}</div>}
    </div>
  );
}
